import React from "react";
import { Dropdown } from "primereact/dropdown";
import { useChartsStore } from "../../store/chartStore";
import { getGroupedBarColorProviders } from "../../Registry/BarColorProviders/barColorRegistry";
import {
  applyBarColorProvider,
  getBarColorProviderEntry,
} from "./barColorSelection.jsx";
import useNavbarDropdown from "./useNavbarDropdown";
import { useNavbarChartControls } from "../../store/deviceSettingsStore";
import { barColorModalRef } from "./barColorModalRef";

const BarColorDropdown = () => {
  const selectedChart = useChartsStore((state) => state.getSelectedChart());
  const selectedBarColorProvider =
    selectedChart?.barColorProvider || "traditional";
  const chartId = selectedChart?.id || null;
  const activeIndicators = selectedChart?.indicators || [];
  const { dropdownRef, onShow, onHide } = useNavbarDropdown();
  // Navbar sem os controles de gráfico (device-setting; reativo p/ toggle vivo)
  const extendedMenuMode = !useNavbarChartControls();

  // Get grouped bar color providers with availability info
  const barColorOptionGroups = getGroupedBarColorProviders(
    activeIndicators,
  ).map((group) => ({
    label: group.label,
    items: group.items.map((provider) => ({
      label: provider.displayName,
      value: provider.key,
      description: provider.description,
      isAvailable: provider.isAvailable,
      requiresIndicators: provider.requiresIndicators || [],
    })),
  }));

  const barColorValueTemplate = () => {
    // Find the current provider display name from all providers
    const currentProvider = getBarColorProviderEntry(selectedBarColorProvider);
    const fullName = currentProvider ? currentProvider.displayName : "Cores";

    // Create shorter display name for button
    const shortNames = {
      traditional: "Tradicional",
      previousClose: "Fech. Anterior",
      volumeComparison: "Volume",
      "indicator:elderImpulse": "Elder Impulse",
      "indicator:rsi": "RSI",
      "indicator:stochastic": "Stochastic",
      "indicator:macd": "MACD",
    };
    const displayText = shortNames[selectedBarColorProvider] || fullName;

    // First letter for mobile view
    const mobileLetters = {
      traditional: "T",
      previousClose: "F",
      volumeComparison: "V",
      "indicator:elderImpulse": "E",
      "indicator:rsi": "R",
      "indicator:stochastic": "S",
      "indicator:macd": "M",
    };
    const mobileLetter =
      mobileLetters[selectedBarColorProvider] || displayText.charAt(0);

    return (
      <div className="navbar-item-content">
        <i className="pi pi-palette navbar-item-icon"></i>
        <span className="navbar-item-label chart-type-label" title={fullName}>
          {displayText}
        </span>
        <span className="bar-color-mobile-label" title={fullName}>
          {mobileLetter}
        </span>
      </div>
    );
  };

  const barColorItemTemplate = (option) => (
    <div
      className="dropdown-item"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        justifyContent: "space-between",
        padding: "8px 12px",
      }}
    >
      <span style={{ fontWeight: "500", fontSize: "13px" }}>
        {option.label}
      </span>
      {/* Show badge if provider requires indicators */}
      {option.isAvailable === false && (
        <span
          style={{
            fontSize: "10px",
            padding: "2px 8px",
            borderRadius: "12px",
            backgroundColor: "#ff9800",
            color: "white",
            fontWeight: "600",
            whiteSpace: "nowrap",
          }}
        >
          Requer indicador
        </span>
      )}
    </div>
  );

  // Full selection flow (missing-indicator confirm, chart-type confirm,
  // color-only cleanup) lives in barColorSelection.jsx — shared with the
  // chart context menu.
  const onChange = (e) => {
    applyBarColorProvider(chartId, selectedChart, e.value, () => {
      // Close the dropdown programmatically
      if (dropdownRef.current) {
        dropdownRef.current.hide();
      }
    });
  };

  // Extended menu ON: the navbar control and the context menu share ONE
  // surface — the BarColorModal (full labels; the 130px dropdown wraps long
  // provider names). The modal itself is mounted ONCE at the Navbar root (NOT
  // here): this dropdown only exists inside viewport-conditional slots, so a
  // modal owned by it would be unmounted on phones — leaving the context
  // menu's "Cores…" pointing at a null ref. Extended menu OFF: legacy
  // Dropdown, byte-identical behavior.
  if (extendedMenuMode) {
    return (
      <button
        type="button"
        className="navbar-button dropdown-btn"
        style={{ width: "130px", border: "none", borderRadius: "0px" }}
        onClick={() => barColorModalRef.current?.open()}
        data-evo-id="navbar-bar-color-dropdown"
      >
        {barColorValueTemplate()}
      </button>
    );
  }

  return (
    <Dropdown
      ref={dropdownRef}
      value={selectedBarColorProvider}
      options={barColorOptionGroups}
      optionGroupLabel="label"
      optionGroupChildren="items"
      onChange={onChange}
      onShow={onShow}
      onHide={onHide}
      placeholder="Cores"
      className="time-dropdown no-scroll-dropdown navbar-button"
      appendTo={document.body}
      style={{
        width: "130px",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        border: "none",
        borderRadius: "0px",
      }}
      valueTemplate={barColorValueTemplate}
      itemTemplate={barColorItemTemplate}
      panelStyle={{ minWidth: "300px" }}
      data-evo-id="navbar-bar-color-dropdown"
    />
  );
};

export default BarColorDropdown;
